import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/FlightResult.css';
import { formatCurrencyParts } from '../utils/pricing';

const AIRLINE_NAMES = {
  A1: 'A.P.G. Distribution System',
  AA: 'American Airlines',
  AC: 'Air Canada',
  AF: 'Air France',
  AI: 'Air India',
  AM: 'Aeromexico',
  AS: 'Alaska Airlines',
  AY: 'Finnair',
  AZ: 'ITA Airways',
  BA: 'British Airways',
  BR: 'EVA Air',
  CA: 'Air China',
  CI: 'China Airlines',
  CX: 'Cathay Pacific',
  CZ: 'China Southern Airlines',
  DL: 'Delta Air Lines',
  EK: 'Emirates',
  ET: 'Ethiopian Airlines',
  EY: 'Etihad Airways',
  FJ: 'Fiji Airways',
  GA: 'Garuda Indonesia',
  GF: 'Gulf Air',
  HU: 'Hainan Airlines',
  IB: 'Iberia',
  JL: 'Japan Airlines',
  JQ: 'Jetstar Airways',
  KE: 'Korean Air',
  KL: 'KLM Royal Dutch Airlines',
  KU: 'Kuwait Airways',
  LA: 'LATAM Airlines',
  LH: 'Lufthansa',
  LO: 'LOT Polish Airlines',
  LX: 'SWISS',
  MH: 'Malaysia Airlines',
  MU: 'China Eastern Airlines',
  NH: 'All Nippon Airways',
  NZ: 'Air New Zealand',
  OA: 'Olympic Air',
  OD: 'Batik Air Malaysia',
  OS: 'Austrian Airlines',
  OZ: 'Asiana Airlines',
  PR: 'Philippine Airlines',
  QF: 'Qantas',
  QR: 'Qatar Airways',
  RO: 'Tarom',
  SA: 'South African Airways',
  SK: 'Scandinavian Airlines',
  SN: 'Brussels Airlines',
  SQ: 'Singapore Airlines',
  SU: 'Aeroflot',
  SV: 'Saudia',
  TG: 'Thai Airways',
  TK: 'Turkish Airlines',
  TP: 'TAP Air Portugal',
  UL: 'SriLankan Airlines',
  UX: 'Air Europa',
  VA: 'Virgin Australia',
  VJ: 'VietJet Air',
  VN: 'Vietnam Airlines',
  VS: 'Virgin Atlantic',
  WY: 'Oman Air',
  XQ: 'SunExpress',
  ZH: 'Shenzhen Airlines',
  '6E': 'IndiGo',
  '9W': 'Jet Airways',
  GP: 'APG Airlines',
  HR: 'Hahn Air',
};

const AGGREGATOR_CARRIER_CODES = new Set(['A1', 'GP', 'HR']);

const AIRCRAFT_NAMES = {
  '321': 'Airbus A321', '32A': 'Airbus A321neo', '32B': 'Airbus A321XLR',
  '320': 'Airbus A320', '32N': 'Airbus A320neo',
  '319': 'Airbus A319', '31N': 'Airbus A319neo',
  '359': 'Airbus A350-900', '351': 'Airbus A350-1000',
  '333': 'Airbus A330-300', '332': 'Airbus A330-200',
  '388': 'Airbus A380',
  '789': 'Boeing 787-9', '788': 'Boeing 787-8', '78X': 'Boeing 787-10',
  '77W': 'Boeing 777-300ER', '779': 'Boeing 777X', '773': 'Boeing 777-300',
  '738': 'Boeing 737-800', '73H': 'Boeing 737-800', '7M8': 'Boeing 737 MAX 8',
  '744': 'Boeing 747-400', '748': 'Boeing 747-8',
  'AT7': 'ATR 72', 'E90': 'Embraer 190', 'E75': 'Embraer 175',
};

const AIRPORT_DETAILS = {
  // Australia
  SYD: { city: 'Sydney',            country: 'Australia' },
  MEL: { city: 'Melbourne',         country: 'Australia' },
  BNE: { city: 'Brisbane',          country: 'Australia' },
  PER: { city: 'Perth',             country: 'Australia' },
  ADL: { city: 'Adelaide',          country: 'Australia' },
  OOL: { city: 'Gold Coast',        country: 'Australia' },
  MCY: { city: 'Sunshine Coast',    country: 'Australia' },
  CNS: { city: 'Cairns',            country: 'Australia' },
  TSV: { city: 'Townsville',        country: 'Australia' },
  MKY: { city: 'Mackay',            country: 'Australia' },
  ROK: { city: 'Rockhampton',       country: 'Australia' },
  AVV: { city: 'Avalon',            country: 'Australia' },
  NTL: { city: 'Newcastle',         country: 'Australia' },
  CFS: { city: 'Coffs Harbour',     country: 'Australia' },
  BNK: { city: 'Ballina',           country: 'Australia' },
  DDD: { city: 'Dubbo',             country: 'Australia' },
  TMW: { city: 'Tamworth',          country: 'Australia' },
  HBA: { city: 'Hobart',            country: 'Australia' },
  CBR: { city: 'Canberra',          country: 'Australia' },
  DRW: { city: 'Darwin',            country: 'Australia' },
  ASP: { city: 'Alice Springs',     country: 'Australia' },
  LST: { city: 'Launceston',        country: 'Australia' },
  BME: { city: 'Broome',            country: 'Australia' },
  PHE: { city: 'Port Hedland',      country: 'Australia' },
  KTA: { city: 'Karratha',          country: 'Australia' },
  GET: { city: 'Geraldton',         country: 'Australia' },
  KGI: { city: 'Kalgoorlie',        country: 'Australia' },
  ESP: { city: 'Esperance',         country: 'Australia' },
  // India — major hubs
  DEL: { city: 'New Delhi',         country: 'India' },
  BOM: { city: 'Mumbai',            country: 'India' },
  BLR: { city: 'Bengaluru',         country: 'India' },
  MAA: { city: 'Chennai',           country: 'India' },
  HYD: { city: 'Hyderabad',         country: 'India' },
  CCU: { city: 'Kolkata',           country: 'India' },
  // India — Kerala
  COK: { city: 'Kochi',             country: 'India' },
  TRV: { city: 'Thiruvananthapuram', country: 'India' },
  CCJ: { city: 'Kozhikode',         country: 'India' },
  CNN: { city: 'Kannur',            country: 'India' },
  // India — Tamil Nadu
  CJB: { city: 'Coimbatore',        country: 'India' },
  IXM: { city: 'Madurai',           country: 'India' },
  TRZ: { city: 'Tiruchirappalli',   country: 'India' },
  TCR: { city: 'Thoothukudi',       country: 'India' },
  TIR: { city: 'Tirupati',          country: 'India' },
  // India — Andhra Pradesh & Telangana
  VTZ: { city: 'Visakhapatnam',     country: 'India' },
  VGA: { city: 'Vijayawada',        country: 'India' },
  RJA: { city: 'Rajahmundry',       country: 'India' },
  // India — Karnataka
  IXE: { city: 'Mangaluru',         country: 'India' },
  HBX: { city: 'Hubballi',          country: 'India' },
  // India — Goa
  GOI: { city: 'Goa',               country: 'India' },
  // India — Maharashtra
  PNQ: { city: 'Pune',              country: 'India' },
  NAG: { city: 'Nagpur',            country: 'India' },
  IXU: { city: 'Aurangabad',        country: 'India' },
  ISK: { city: 'Nashik',            country: 'India' },
  KLH: { city: 'Kolhapur',          country: 'India' },
  SSE: { city: 'Solapur',           country: 'India' },
  NDC: { city: 'Nanded',            country: 'India' },
  // India — Gujarat
  AMD: { city: 'Ahmedabad',         country: 'India' },
  BDQ: { city: 'Vadodara',          country: 'India' },
  STV: { city: 'Surat',             country: 'India' },
  RAJ: { city: 'Rajkot',            country: 'India' },
  BHJ: { city: 'Bhuj',              country: 'India' },
  JGA: { city: 'Jamnagar',          country: 'India' },
  PBD: { city: 'Porbandar',         country: 'India' },
  DIU: { city: 'Diu',               country: 'India' },
  // India — Rajasthan
  JAI: { city: 'Jaipur',            country: 'India' },
  JDH: { city: 'Jodhpur',           country: 'India' },
  UDR: { city: 'Udaipur',           country: 'India' },
  JSA: { city: 'Jaisalmer',         country: 'India' },
  BKB: { city: 'Bikaner',           country: 'India' },
  KQH: { city: 'Ajmer',             country: 'India' },
  // India — Punjab & Haryana
  ATQ: { city: 'Amritsar',          country: 'India' },
  IXC: { city: 'Chandigarh',        country: 'India' },
  BUP: { city: 'Bathinda',          country: 'India' },
  IXP: { city: 'Pathankot',         country: 'India' },
  // India — Himachal Pradesh
  DHM: { city: 'Dharamsala',        country: 'India' },
  KUU: { city: 'Kullu',             country: 'India' },
  SLV: { city: 'Shimla',            country: 'India' },
  // India — Jammu & Kashmir / Ladakh
  SXR: { city: 'Srinagar',          country: 'India' },
  IXJ: { city: 'Jammu',             country: 'India' },
  IXL: { city: 'Leh',               country: 'India' },
  // India — Uttar Pradesh
  LKO: { city: 'Lucknow',           country: 'India' },
  VNS: { city: 'Varanasi',          country: 'India' },
  AGR: { city: 'Agra',              country: 'India' },
  IXD: { city: 'Prayagraj',         country: 'India' },
  GOP: { city: 'Gorakhpur',         country: 'India' },
  KNU: { city: 'Kanpur',            country: 'India' },
  // India — Uttarakhand
  DED: { city: 'Dehradun',          country: 'India' },
  PGH: { city: 'Pantnagar',         country: 'India' },
  // India — Bihar & Jharkhand
  PAT: { city: 'Patna',             country: 'India' },
  GAY: { city: 'Gaya',              country: 'India' },
  IXR: { city: 'Ranchi',            country: 'India' },
  DBR: { city: 'Darbhanga',         country: 'India' },
  // India — West Bengal & Odisha
  IXB: { city: 'Bagdogra',          country: 'India' },
  BBI: { city: 'Bhubaneswar',       country: 'India' },
  RDP: { city: 'Durgapur',          country: 'India' },
  // India — Madhya Pradesh & Chhattisgarh
  BHO: { city: 'Bhopal',            country: 'India' },
  IDR: { city: 'Indore',            country: 'India' },
  RPR: { city: 'Raipur',            country: 'India' },
  JLR: { city: 'Jabalpur',          country: 'India' },
  GWL: { city: 'Gwalior',           country: 'India' },
  // India — Northeast
  GAU: { city: 'Guwahati',          country: 'India' },
  IXA: { city: 'Agartala',          country: 'India' },
  IXS: { city: 'Silchar',           country: 'India' },
  DIB: { city: 'Dibrugarh',         country: 'India' },
  JRH: { city: 'Jorhat',            country: 'India' },
  TEZ: { city: 'Tezpur',            country: 'India' },
  IMF: { city: 'Imphal',            country: 'India' },
  SHL: { city: 'Shillong',          country: 'India' },
  AJL: { city: 'Aizawl',            country: 'India' },
  DMU: { city: 'Dimapur',           country: 'India' },
  // India — Islands
  IXZ: { city: 'Port Blair',        country: 'India' },
  AGX: { city: 'Agatti',            country: 'India' },
  // New Zealand & Pacific
  AKL: { city: 'Auckland',          country: 'New Zealand' },
  CHC: { city: 'Christchurch',      country: 'New Zealand' },
  WLG: { city: 'Wellington',        country: 'New Zealand' },
  NAN: { city: 'Nadi',              country: 'Fiji' },
  // Southeast Asia
  SIN: { city: 'Singapore',         country: 'Singapore' },
  KUL: { city: 'Kuala Lumpur',      country: 'Malaysia' },
  BKK: { city: 'Bangkok',           country: 'Thailand' },
  HKT: { city: 'Phuket',            country: 'Thailand' },
  DPS: { city: 'Denpasar',          country: 'Indonesia' },
  CGK: { city: 'Jakarta',           country: 'Indonesia' },
  MNL: { city: 'Manila',            country: 'Philippines' },
  SGN: { city: 'Ho Chi Minh City',  country: 'Vietnam' },
  HAN: { city: 'Hanoi',             country: 'Vietnam' },
  RGN: { city: 'Yangon',            country: 'Myanmar' },
  // Northeast Asia
  HKG: { city: 'Hong Kong',         country: 'Hong Kong' },
  NRT: { city: 'Tokyo',             country: 'Japan' },
  HND: { city: 'Tokyo',             country: 'Japan' },
  KIX: { city: 'Osaka',             country: 'Japan' },
  ICN: { city: 'Seoul',             country: 'South Korea' },
  PVG: { city: 'Shanghai',          country: 'China' },
  PEK: { city: 'Beijing',           country: 'China' },
  CAN: { city: 'Guangzhou',         country: 'China' },
  TPE: { city: 'Taipei',            country: 'Taiwan' },
  // South Asia (other)
  CMB: { city: 'Colombo',           country: 'Sri Lanka' },
  KTM: { city: 'Kathmandu',         country: 'Nepal' },
  MLE: { city: 'Male',              country: 'Maldives' },
  DAC: { city: 'Dhaka',             country: 'Bangladesh' },
  KHI: { city: 'Karachi',           country: 'Pakistan' },
  LHE: { city: 'Lahore',            country: 'Pakistan' },
  ISB: { city: 'Islamabad',         country: 'Pakistan' },
  // Middle East
  DXB: { city: 'Dubai',             country: 'UAE' },
  AUH: { city: 'Abu Dhabi',         country: 'UAE' },
  DOH: { city: 'Doha',              country: 'Qatar' },
  RUH: { city: 'Riyadh',            country: 'Saudi Arabia' },
  JED: { city: 'Jeddah',            country: 'Saudi Arabia' },
  KWI: { city: 'Kuwait City',       country: 'Kuwait' },
  MCT: { city: 'Muscat',            country: 'Oman' },
  TLV: { city: 'Tel Aviv',          country: 'Israel' },
  CAI: { city: 'Cairo',             country: 'Egypt' },
  // Europe
  LHR: { city: 'London',            country: 'United Kingdom' },
  LGW: { city: 'London',            country: 'United Kingdom' },
  CDG: { city: 'Paris',             country: 'France' },
  ORY: { city: 'Paris',             country: 'France' },
  FRA: { city: 'Frankfurt',         country: 'Germany' },
  MUC: { city: 'Munich',            country: 'Germany' },
  AMS: { city: 'Amsterdam',         country: 'Netherlands' },
  ZRH: { city: 'Zurich',            country: 'Switzerland' },
  VIE: { city: 'Vienna',            country: 'Austria' },
  FCO: { city: 'Rome',              country: 'Italy' },
  MXP: { city: 'Milan',             country: 'Italy' },
  BCN: { city: 'Barcelona',         country: 'Spain' },
  MAD: { city: 'Madrid',            country: 'Spain' },
  LIS: { city: 'Lisbon',            country: 'Portugal' },
  ATH: { city: 'Athens',            country: 'Greece' },
  IST: { city: 'Istanbul',          country: 'Turkey' },
  CPH: { city: 'Copenhagen',        country: 'Denmark' },
  OSL: { city: 'Oslo',              country: 'Norway' },
  ARN: { city: 'Stockholm',         country: 'Sweden' },
  HEL: { city: 'Helsinki',          country: 'Finland' },
  BRU: { city: 'Brussels',          country: 'Belgium' },
  DUB: { city: 'Dublin',            country: 'Ireland' },
  // North America
  JFK: { city: 'New York',          country: 'United States' },
  EWR: { city: 'Newark',            country: 'United States' },
  LAX: { city: 'Los Angeles',       country: 'United States' },
  SFO: { city: 'San Francisco',     country: 'United States' },
  ORD: { city: 'Chicago',           country: 'United States' },
  ATL: { city: 'Atlanta',           country: 'United States' },
  MIA: { city: 'Miami',             country: 'United States' },
  DFW: { city: 'Dallas',            country: 'United States' },
  SEA: { city: 'Seattle',           country: 'United States' },
  LAS: { city: 'Las Vegas',         country: 'United States' },
  BOS: { city: 'Boston',            country: 'United States' },
  YYZ: { city: 'Toronto',           country: 'Canada' },
  YVR: { city: 'Vancouver',         country: 'Canada' },
  // South America
  GRU: { city: 'São Paulo',         country: 'Brazil' },
  EZE: { city: 'Buenos Aires',      country: 'Argentina' },
  SCL: { city: 'Santiago',          country: 'Chile' },
  LIM: { city: 'Lima',              country: 'Peru' },
  BOG: { city: 'Bogota',            country: 'Colombia' },
  // Africa
  JNB: { city: 'Johannesburg',      country: 'South Africa' },
  CPT: { city: 'Cape Town',         country: 'South Africa' },
  NBO: { city: 'Nairobi',           country: 'Kenya' },
  ADD: { city: 'Addis Ababa',       country: 'Ethiopia' },
};

const CABIN_ALLOWANCE_BY_CLASS = {
  economy: { checkIn: '25 kg (1 piece)', cabin: '7 kg' },
  'premium economy': { checkIn: '30 kg (2 pieces)', cabin: '10 kg' },
  business: { checkIn: '40 kg (2 pieces)', cabin: '14 kg' },
  'first class': { checkIn: '50 kg (2 pieces)', cabin: '15 kg' },
};

const AIRLINE_ALLOWANCE_OVERRIDES = {
  '6E': { checkIn: '15 kg (1 piece)', cabin: '7 kg' },
  AI: { checkIn: '25 kg (1 piece)', cabin: '7 kg' },
  EK: { checkIn: '30 kg (1 piece)', cabin: '7 kg' },
  EY: { checkIn: '30 kg (1 piece)', cabin: '7 kg' },
  QR: { checkIn: '30 kg (1 piece)', cabin: '7 kg' },
  UL: { checkIn: '30 kg (1 piece)', cabin: '7 kg' },
  SQ: { checkIn: '30 kg (1 piece)', cabin: '7 kg' },
  QF: { checkIn: '30 kg (1 piece)', cabin: '7 kg' },
  VN: { checkIn: '23 kg (1 piece)', cabin: '12 kg' },
};

function formatAircraft(code) {
  if (!code) return null;
  return AIRCRAFT_NAMES[code.toUpperCase()] || `Aircraft ${code}`;
}

function getFlightParts(code) {
  const normalized = String(code || '').trim().toUpperCase();
  const compact = normalized.replace(/[\s-]/g, '');
  const carrier = compact.slice(0, 2);
  const number = compact.slice(2);

  return {
    carrier,
    number,
  };
}

function getAirportInfo(iataCode) {
  const code = String(iataCode || '').toUpperCase();
  if (!code) {
    return { code: '', cityCountry: 'Airport unavailable' };
  }

  const details = AIRPORT_DETAILS[code];
  if (!details) {
    return { code, cityCountry: code };
  }

  return { code, cityCountry: `${details.city}, ${details.country}` };
}

function getAirportCodeFromStopValue(stopValue) {
  const raw = String(stopValue || '').trim();
  if (!raw) {
    return null;
  }

  const normalized = raw.toUpperCase();
  if (/^[A-Z]{3}$/.test(normalized)) {
    return normalized;
  }

  const cityName = raw.split(',')[0].trim().toLowerCase();
  const matchedEntry = Object.entries(AIRPORT_DETAILS).find(([, details]) => (
    String(details.city || '').toLowerCase() === cityName
  ));

  return matchedEntry ? matchedEntry[0] : null;
}

function getStopAirportCodes(segments, stopCities) {
  if (Array.isArray(segments) && segments.length > 1) {
    const codesFromSegments = segments
      .slice(0, -1)
      .map((segment) => String(segment?.arrivalAirport || '').trim().toUpperCase())
      .filter((code) => /^[A-Z]{3}$/.test(code));

    if (codesFromSegments.length > 0) {
      return [...new Set(codesFromSegments)];
    }
  }

  const codesFromStops = (Array.isArray(stopCities) ? stopCities : [])
    .map(getAirportCodeFromStopValue)
    .filter(Boolean);

  return [...new Set(codesFromStops)];
}

function getAllowanceForSegment(carrierCode, cabinClass) {
  const normalizedCarrier = String(carrierCode || '').toUpperCase();
  const normalizedCabin = String(cabinClass || 'Economy').toLowerCase();
  const byClass = CABIN_ALLOWANCE_BY_CLASS[normalizedCabin] || CABIN_ALLOWANCE_BY_CLASS.economy;
  const override = AIRLINE_ALLOWANCE_OVERRIDES[normalizedCarrier];

  return {
    baggageType: 'ADULT',
    checkIn: override?.checkIn || byClass.checkIn,
    cabin: override?.cabin || byClass.cabin,
  };
}

function getFlightNumberSummary(segments, fallbackParts) {
  if (Array.isArray(segments) && segments.length > 0) {
    const segmentCodes = segments
      .map((segment) => {
        const carrier = String(segment?.carrierCode || '').trim().toUpperCase();
        const number = String(segment?.flightNumber || '').trim();
        if (!carrier || !number) {
          return null;
        }

        return `${carrier} ${number}`;
      })
      .filter(Boolean);

    if (segmentCodes.length > 0) {
      return segmentCodes.join(', ');
    }
  }

  if (fallbackParts?.carrier && fallbackParts?.number) {
    return `${fallbackParts.carrier} ${fallbackParts.number}`;
  }

  return '';
}

function FlightResult({ flight, onSelect, selected, fareOptionCount, badge, showDateHeader }) {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);
  const priceParts = formatCurrencyParts(
    flight.displayPriceAmount ?? flight.price,
    flight.displayCurrency || flight.priceCurrency || 'AUD',
  );
  const priceCurrencyLabel = (flight.displayCurrency || flight.priceCurrency || 'AUD') === 'INR'
    ? '₹'
    : priceParts.currency;

  const handleSelect = () => {
    if (onSelect) { onSelect(); return; }
    navigate('/flight-details', { state: { flight } });
  };

  const stopCount = String(flight.type || '').toLowerCase();
  const isMultiStop = stopCount.includes('stop') && !stopCount.includes('direct');
  const stopCities = flight.stopCities || [];
  const stopDurations = flight.stopDurations || [];
  const segments = flight.segments || [];
  const hasStopDetails = isMultiStop && (segments.length > 0 || stopCities.length > 0);
  const stopAirportCodes = getStopAirportCodes(segments, stopCities);
  const fallbackParts = getFlightParts(flight.code);
  const rawSegmentCarrier = String(segments[0]?.carrierCode || '').toUpperCase();
  const primaryCarrier = rawSegmentCarrier && AGGREGATOR_CARRIER_CODES.has(rawSegmentCarrier) && fallbackParts.carrier
    ? fallbackParts.carrier
    : (rawSegmentCarrier || fallbackParts.carrier);
  const flightNumberSummary = getFlightNumberSummary(segments, fallbackParts);

  const normalizedAirlineCode = String(flight.airline || '').trim().toUpperCase();
  const codeCarrier = String(flight.code || '').trim().toUpperCase().replace(/[\s-]/g, '').slice(0, 2);
  const airlineName = (() => {
    if (codeCarrier && AIRLINE_NAMES[codeCarrier] && !AGGREGATOR_CARRIER_CODES.has(codeCarrier)) {
      return AIRLINE_NAMES[codeCarrier];
    }

    if (primaryCarrier && AIRLINE_NAMES[primaryCarrier]) {
      return AIRLINE_NAMES[primaryCarrier];
    }

    if (normalizedAirlineCode.length <= 3 && AIRLINE_NAMES[normalizedAirlineCode]) {
      return AIRLINE_NAMES[normalizedAirlineCode];
    }

    if (flight.airline && flight.airline.length > 3) {
      return flight.airline;
    }

    return AIRLINE_NAMES[primaryCarrier] || (primaryCarrier ? `Airline ${primaryCarrier}` : 'Airline');
  })();

  // Use Duffel logo URL from API (SVG format for best quality)
  const logoUrl = flight.logoUrl || null;
  
  // FALLBACK: Kiwi.com CDN (commented out - only for testing until Duffel logos verified)
  // const logoUrl = primaryCarrier
  //   ? `https://images.kiwi.com/airlines/64/${primaryCarrier}.png`
  //   : '';
  
  const showSourceBadge = false; // hidden — internal data provider detail, not user-facing

  return (
    <div 
      className={`flight-result${isMultiStop ? ' flight-result--stop' : ''}${selected ? ' flight-result--selected' : ''}${showDetails ? ' flight-result--expanded' : ''}`}
      onClick={handleSelect}
      style={{ cursor: 'pointer' }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(); } }}
    >
      {badge && (
        // Handle both single badge (string) and multiple badges (array)
        Array.isArray(badge) ? (
          badge.map((b, index) => (
            <span key={index} className={`flight-result-badge flight-result-badge--${b.toLowerCase().replace(/\s+/g, '-')}`} aria-label={b}>
              {b}
            </span>
          ))
        ) : (
          <span className={`flight-result-badge flight-result-badge--${badge.toLowerCase().replace(/\s+/g, '-')}`} aria-label={badge}>
            {badge}
          </span>
        )
      )}
      {showDateHeader && flight.departureDate && (
        <div className="flight-date-header" aria-label={`Flight date: ${flight.departureDate}`}>
          📅 {flight.departureDate}
        </div>
      )}
      <div className="flight-airline">
        <div className="airline-brand">
          {logoUrl ? (
            <img
              className="airline-logo"
              src={logoUrl}
              alt={airlineName}
              loading="lazy"
              width="30"
              height="30"
              onError={(event) => {
                event.currentTarget.style.display = 'none';
                const fallback = event.currentTarget.nextElementSibling;
                if (fallback) {
                  fallback.style.display = 'inline-flex';
                }
              }}
            />
          ) : null}
          <span className="airline-logo-fallback" style={logoUrl ? { display: 'none' } : undefined}>
            {primaryCarrier || 'FL'}
          </span>
          <span className="airline-name">{airlineName}</span>
        </div>
        {showSourceBadge && (
          <div className={`source-badge source-${String(flight.source || 'local').toLowerCase()}`}>
            {flight.source || 'Local'}
          </div>
        )}
        {flightNumberSummary && (
          <span className="airline-code">{flightNumberSummary}</span>
        )}
      </div>

      <div className="flight-times">
        <div className="departure">
          <div className="time">{flight.departure}</div>
          <div className="date">{flight.departureDate}</div>
          {flight.from && <div className="city-code">{flight.from}</div>}
        </div>
        <div className="flight-duration">
          <div className="duration-line">
            <span className="duration-dot"></span>
            <div className="duration-track">
              <span className="duration-text">{flight.duration}</span>
            </div>
            <span className="duration-plane">✈</span>
            <span className="duration-dot"></span>
          </div>
          <div className={`flight-type${stopCount.includes('stop') ? ' flight-type--stop' : ' flight-type--direct'}`}>
            {flight.type}
            {stopCount.includes('stop') && hasStopDetails && stopAirportCodes.length > 0 && (
              <span className="flight-type__via">· {stopAirportCodes.join(', ')}</span>
            )}
          </div>
        </div>
        <div className="arrival">
          <div className="time">{flight.arrival}</div>
          <div className="date">{flight.arrivalDate}</div>
          {flight.to && <div className="city-code">{flight.to}</div>}
        </div>
      </div>

      <div className="flight-seats">
        <span className="seats-badge">{flight.class || 'Economy'}</span>
      </div>

      <div className="flight-price-action">
        <div className="flight-price">
          <div className="price-label">From</div>
          <div className="price-amount" aria-label={priceParts.value}>
            <span className="price-currency">{priceCurrencyLabel}</span>
            <span className="price-value">{priceParts.amount}</span>
          </div>
          <div className="price-pax">per person</div>
          <div className="price-taxes">incl. taxes</div>
          {fareOptionCount > 1 && (
            <span className="fare-option-count">{fareOptionCount} fare options</span>
          )}
        </div>
        
        <button
          className="select-btn"
          onClick={handleSelect}
        >
          Select Flight
        </button>
      </div>

      {hasStopDetails && (
        <button
          type="button"
          className="fd-toggle"
          onClick={(e) => { e.stopPropagation(); setShowDetails(v => !v); }}
          aria-expanded={showDetails}
        >
          {showDetails ? '▲ Hide Flight Details' : '▼ View Flight Details'}
        </button>
      )}

      {showDetails && hasStopDetails && (
        <div className="fd-panel" role="region" aria-label="Flight segment details">
          {segments.length > 0 ? (
            segments.map((seg, idx) => {
              const departureAirport = getAirportInfo(seg.departureAirport);
              const arrivalAirport = getAirportInfo(seg.arrivalAirport);
              const allowances = getAllowanceForSegment(seg.carrierCode, flight.class);

              return (
              <React.Fragment key={idx}>
                <div className="fd-segment">
                  <div className="fd-seg-header">
                    <span className="fd-seg-airline-badge">{airlineName}</span>
                    <span className="fd-seg-flight-num">
                      Flight {seg.carrierCode} {seg.flightNumber}
                    </span>
                    {seg.aircraft && (
                      <span className="fd-seg-aircraft-chip">{formatAircraft(seg.aircraft)}</span>
                    )}
                  </div>
                  <div className="fd-seg-times">
                    <div className="fd-seg-point">
                      <div className="fd-seg-big-time">{seg.departureTime}</div>
                      <div className="fd-seg-airport-code">{departureAirport.code}</div>
                      <div className="fd-seg-location">{departureAirport.cityCountry}</div>
                      {seg.departureTerminal && (
                        <div className="fd-seg-terminal">Terminal {seg.departureTerminal}</div>
                      )}
                    </div>
                    <div className="fd-seg-bar">
                      <div className="fd-seg-bar-line">
                        <span className="fd-seg-bar-dot"></span>
                        <div className="fd-seg-bar-track"></div>
                        <span className="fd-seg-bar-plane">✈</span>
                        <div className="fd-seg-bar-track"></div>
                        <span className="fd-seg-bar-dot"></span>
                      </div>
                      <div className="fd-seg-bar-dur">{seg.duration}</div>
                    </div>
                    <div className="fd-seg-point fd-seg-point--right">
                      <div className="fd-seg-big-time">{seg.arrivalTime}</div>
                      <div className="fd-seg-airport-code">{arrivalAirport.code}</div>
                      <div className="fd-seg-location">{arrivalAirport.cityCountry}</div>
                      {seg.arrivalTerminal && (
                        <div className="fd-seg-terminal">Terminal {seg.arrivalTerminal}</div>
                      )}
                    </div>
                  </div>
                  <div className="fd-service-grid">
                    <div className="fd-service-item">
                      <span className="fd-service-label">BAGGAGE</span>
                      <span className="fd-service-value">{allowances.baggageType}</span>
                    </div>
                    <div className="fd-service-item">
                      <span className="fd-service-label">CHECK-IN</span>
                      <span className="fd-service-value">{allowances.checkIn}</span>
                    </div>
                    <div className="fd-service-item">
                      <span className="fd-service-label">CABIN</span>
                      <span className="fd-service-value">{allowances.cabin}</span>
                    </div>
                  </div>
                </div>
                {idx < segments.length - 1 && (
                  <div className="fd-layover">
                    <span className="fd-layover-icon">⏱</span>
                    <span className="fd-layover-text">
                      {stopDurations[idx] || '-'} layover in {stopCities[idx] || seg.arrivalAirport}
                    </span>
                    <span className="fd-layover-change">Change of planes</span>
                  </div>
                )}
              </React.Fragment>
            );
            })
          ) : (
            stopCities.map((stopCity, idx) => (
              <React.Fragment key={`${stopCity}-${idx}`}>
                <div className="fd-segment">
                  <div className="fd-seg-header">
                    <span className="fd-seg-airline-badge">{airlineName}</span>
                    {idx === 0 && <span className="fd-seg-time-badge fd-seg-time-badge--depart">Departs {flight.departure}</span>}
                  </div>
                  <div className="fd-seg-times">
                    <div className="fd-seg-point">
                      <div className="fd-seg-big-time">{idx === 0 ? flight.departure : '-'}</div>
                      <div className="fd-seg-airport-code">{idx === 0 ? flight.from : stopCities[idx - 1]}</div>
                    </div>
                    <div className="fd-seg-bar">
                      <div className="fd-seg-bar-line">
                        <span className="fd-seg-bar-dot"></span>
                        <div className="fd-seg-bar-track"></div>
                        <span className="fd-seg-bar-plane">✈</span>
                        <div className="fd-seg-bar-track"></div>
                        <span className="fd-seg-bar-dot"></span>
                      </div>
                    </div>
                    <div className="fd-seg-point fd-seg-point--right">
                      <div className="fd-seg-airport-code">{stopCity}</div>
                    </div>
                  </div>
                </div>
                <div className="fd-layover">
                  <span className="fd-layover-icon">⏱</span>
                  <span className="fd-layover-text">{stopDurations[idx] || '-'} layover in {stopCity}</span>
                  <span className="fd-layover-change">Change of planes</span>
                </div>
                {idx === stopCities.length - 1 && (
                  <div className="fd-segment">
                    <div className="fd-seg-header">
                      <span className="fd-seg-airline-badge">{airlineName}</span>
                      <span className="fd-seg-time-badge fd-seg-time-badge--arrive">Arrives {flight.arrival}</span>
                    </div>
                    <div className="fd-seg-times">
                      <div className="fd-seg-point"><div className="fd-seg-airport-code">{stopCity}</div></div>
                      <div className="fd-seg-bar"><div className="fd-seg-bar-line"><span className="fd-seg-bar-dot"></span><div className="fd-seg-bar-track"></div><span className="fd-seg-bar-plane">✈</span><div className="fd-seg-bar-track"></div><span className="fd-seg-bar-dot"></span></div></div>
                      <div className="fd-seg-point fd-seg-point--right"><div className="fd-seg-airport-code">{flight.to}</div></div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default FlightResult;
